import { useState, useEffect } from 'react';

import TextInput from '../inputs/TextInput';
import Button from '../inputs/Button';
import { incrementNameNoConflicts } from '../../utils';
import usePackageNameValidation from '../../hooks/usePackageNameValidation';

type PackageNameFormProps = {
  onBlur: (value: string) => void;
  onSubmit: (packageNameInput: string, callbackFn: () => void) => void;
  initialValue: string;
  canSave: boolean;
  hasStateChanged: boolean;
  isSavedPackage: boolean;
  setNameError: (value: boolean) => void;
};

export default function PackageNameForm({
  onBlur,
  onSubmit,
  initialValue,
  canSave,
  hasStateChanged,
  isSavedPackage,
  setNameError,
}: PackageNameFormProps) {
  const [value, setValue] = useState(initialValue);

  const { packageWithNameAlreadyExists, takenNames } =
    usePackageNameValidation(value);

  useEffect(() => {
    if (
      initialValue &&
      packageWithNameAlreadyExists &&
      takenNames &&
      initialValue === value &&
      !isSavedPackage
    ) {
      const incrementedName = incrementNameNoConflicts(
        initialValue,
        takenNames
      );
      if (typeof onBlur === 'function') {
        setValue(incrementedName);
        onBlur(incrementedName);
      }
    }
  }, [
    packageWithNameAlreadyExists,
    initialValue,
    isSavedPackage,
    onBlur,
    takenNames,
    value,
  ]);

  function handleSubmit(e) {
    e.preventDefault();

    if (packageWithNameAlreadyExists && !isSavedPackage) {
      setNameError(true);
      return;
    }

    if (canSave) {
      onSubmit(value);
    }
    setNameError(false);
  }

  useEffect(() => {
    if (typeof initialValue === 'string') {
      setValue(initialValue);
    }
  }, [initialValue]);

  return (
    <form className="flex flex-row space-x-3" onSubmit={handleSubmit}>
      <TextInput
        type="text"
        value={value}
        onChange={(e) => {
          const { value } = e.target;
          setValue(value);
        }}
        onBlur={() => onBlur(value)}
        placeholder="Untitled Enhancement Package"
        disabled={isSavedPackage}
      />
      <Button
        type="submit"
        disabled={!canSave}
        primary={canSave && hasStateChanged}
        secondary={canSave && !hasStateChanged}
      >
        Save Package
      </Button>
    </form>
  );
}
